import { useEffect, useMemo, useState } from "react";
import { useData } from "@core/lib/useData";
import { useViewState } from "@core/lib/useViewState";
import LazyMap, { type Basemap, type MapPoint } from "@core/components/LazyMap";
import GroupFilter from "@core/components/GroupFilter";
import TrendChart, { type Series } from "@core/components/TrendChart";
import ShareCard from "@core/components/ShareCard";
import { Footer, Header, Splash, Tabs, type Pane } from "@core/components/AppShell";
import WaterWatchCredit from "@core/components/WaterWatchCredit";
import InfoButton from "@core/components/InfoDialog";
import { ABOUT } from "./lib/about";
import { formatValue, mean, plural } from "@core/lib/format";
import StationList from "./components/StationList";
import StationDetail from "./components/StationDetail";
import { GRADES, GRADE_ORDER, SCORE_BANDS } from "./lib/grades";
import type { Payload, YearRow } from "./lib/types";

/** Metrics the station-year series can be charted on. The component scores
 *  share the Total Score scale; the raw counts do not, so they get their own. */
const METRICS = {
  score: {
    label: "Total score", unit: "score", pick: (r: YearRow) => r.sc,
    color: "#1A7A3C", bands: true, digits: 0, domain: [0, 100] as [number, number],
  },
  taxa_score: {
    label: "Taxa score", unit: "score", pick: (r: YearRow) => r.ts,
    color: "#0E6CA8", bands: false, digits: 0, domain: [0, 20] as [number, number],
  },
  ept_score: {
    label: "EPT score", unit: "score", pick: (r: YearRow) => r.es,
    color: "#008DAF", bands: false, digits: 0, domain: [0, 30] as [number, number],
  },
  psens_score: {
    label: "P-sensitive score", unit: "score", pick: (r: YearRow) => r.pss,
    color: "#388173", bands: false, digits: 0, domain: [0, 50] as [number, number],
  },
  taxa: {
    label: "Total taxa", unit: "taxa", pick: (r: YearRow) => r.tx,
    color: "#915530", bands: false, digits: 1, domain: undefined,
  },
  ept: {
    label: "EPT families", unit: "families", pick: (r: YearRow) => r.ep,
    color: "#C99700", bands: false, digits: 1, domain: undefined,
  },
  psens: {
    label: "P-sensitive organisms", unit: "organisms", pick: (r: YearRow) => r.ps,
    color: "#6FA12E", bands: false, digits: 1, domain: undefined,
  },
} as const;

type MetricKey = keyof typeof METRICS;

/** What the app opens on. Named explicitly rather than taken from the first
 *  key, since the list below is ordered alphabetically for the reader: the
 *  total score is the headline the grades are cut from. */
const DEFAULT_METRIC: MetricKey = "score";

/** Alphabetical by label, in the chart ribbon. */
const METRIC_KEYS = (Object.keys(METRICS) as MetricKey[]).sort((a, b) =>
  METRICS[a].label.localeCompare(METRICS[b].label, undefined, { sensitivity: "base" }),
);

export default function App() {
  const { data, error, loading, refreshing, lastRefresh, refresh } = useData<Payload>();
  const view = useViewState("stream");
  const [basemap, setBasemap] = useState<Basemap>("satellite");
  const [pane, setPane] = useState<Pane>("map");

  const metric: MetricKey = (() => {
    const m = view.params.get("metric");
    return m && (METRIC_KEYS as string[]).includes(m) ? (m as MetricKey) : DEFAULT_METRIC;
  })();

  useEffect(() => {
    if (view.stationId && window.innerWidth <= 1060) setPane("map");
  }, [view.stationId]);

  const selected = useMemo(
    () => data?.stations.find((s) => s.key === view.stationId) ?? null,
    [data, view.stationId],
  );
  const activeStream = view.group ?? null;

  const inScope = useMemo(() => {
    if (!data) return [];
    return data.stations.filter((s) => !activeStream || s.stream === activeStream);
  }, [data, activeStream]);

  const rowsInScope = useMemo(() => {
    if (!data) return [];
    if (selected) return data.years.filter((r) => r.s === selected.key);
    const ids = new Set(inScope.map((s) => s.key));
    return data.years.filter((r) => ids.has(r.s));
  }, [data, selected, inScope]);

  const points: MapPoint[] = useMemo(() => {
    if (!data) return [];
    const scope = new Set(inScope.map((s) => s.key));
    return data.stations.map((s) => ({
      id: s.key,
      lat: s.lat,
      lng: s.lng,
      color: GRADES[s.grade].color,
      label: `${s.siteId} — ${s.name} (grade ${GRADES[s.grade].label})`,
      muted: !scope.has(s.key),
    }));
  }, [data, inScope]);

  const fitTo = useMemo(() => points.filter((p) => !p.muted), [points]);

  if (loading) return <Splash>Loading stream monitoring data…</Splash>;
  if (error || !data) {
    return (
      <Splash tone="error">
        <strong>Could not load stream monitoring data.</strong>
        <span>{error}</span>
        <button className="btn" onClick={refresh}>Try again</button>
      </Splash>
    );
  }

  const legend = GRADE_ORDER
    .map((g) => ({
      key: g,
      label: GRADES[g].label,
      color: GRADES[g].color,
      n: inScope.filter((s) => s.grade === g).length,
    }))
    .filter((l) => l.n > 0);

  const M = METRICS[metric];

  const series: Series[] = (() => {
    if (selected) {
      return [{
        key: selected.key, label: selected.siteId, color: M.color, kind: "line" as const,
        points: rowsInScope
          .map((r) => ({ x: r.y, y: M.pick(r), note: `grade ${r.g}` }))
          .filter((p): p is { x: number; y: number; note: string } => p.y !== null),
      }];
    }
    if (activeStream && inScope.length > 1 && inScope.length <= 8) {
      const palette = ["#1A7A3C", "#0E6CA8", "#C99700", "#008DAF", "#915530", "#6FA12E", "#A4161A", "#485070"];
      return inScope.map((s, i) => ({
        key: s.key, label: s.siteId, color: palette[i % palette.length], kind: "line" as const,
        points: rowsInScope
          .filter((r) => r.s === s.key)
          .map((r) => ({ x: r.y, y: M.pick(r), note: s.name }))
          .filter((p): p is { x: number; y: number; note: string } => p.y !== null),
      })).filter((s) => s.points.length > 0);
    }
    const byYear = new Map<number, number[]>();
    for (const r of rowsInScope) {
      const v = M.pick(r);
      if (v === null) continue;
      (byYear.get(r.y) ?? byYear.set(r.y, []).get(r.y)!).push(v);
    }
    return [{
      key: "mean",
      label: activeStream ?? "All streams",
      color: M.color,
      kind: "line" as const,
      points: [...byYear.entries()]
        .map(([y, vs]) => ({ x: y, y: mean(vs) as number, note: `mean of ${plural(vs.length, "site")}` }))
        .sort((a, b) => a.x - b.x),
    }];
  })();

  const scopeName = activeStream;
  const chartTitle = selected
    ? `${selected.siteId} — ${M.label.toLowerCase()}`
    : `${scopeName ?? "All streams"} — average ${M.label.toLowerCase()} by year`;
  const pointCount = series.reduce((a, s) => a + s.points.length, 0);

  return (
    <div className="app">
      <Header
        program="Volunteer Stream Monitoring"
        programShort="Stream Monitoring"
        legend={legend}
        basemap={basemap}
        onBasemap={setBasemap}
        refreshing={refreshing}
        lastRefresh={lastRefresh}
        onRefresh={refresh}
        extra={<InfoButton content={ABOUT} />}
      />

      <div className="subbar">
        <GroupFilter
          noun="Stream"
          nounPlural="Streams"
          options={data.streams}
          value={activeStream}
          onChange={view.selectGroup}
          link={activeStream ? view.groupLink(activeStream) : null}
        />
        {selected && (
          <button className="btn ghost sm" onClick={() => view.selectStation(null)}>
            Clear site
          </button>
        )}
        <span className="subbar-spacer" />
        <span className="subbar-note">
          {scopeName
            ? `${plural(inScope.length, "site")} · ${scopeName}`
            : `${plural(data.stations.length, "site")} · ${data.streams.length} streams`}
        </span>
      </div>

      <Tabs pane={pane} onPane={setPane} trendLabel="Scores" />

      <main className={"body" + (selected || activeStream ? " has-detail" : "")}>
        <section className={"pane pane-list" + (pane === "list" ? " show" : "")}>
          <StationList
            stations={inScope}
            selectedId={view.stationId}
            onSelect={view.selectStation}
            scopeName={scopeName}
          />
        </section>

        <section className={"pane pane-map" + (pane === "map" ? " show" : "")}>
          <LazyMap
            points={points}
            selectedId={view.stationId}
            fitTo={fitTo}
            basemap={basemap}
            token={data.mapboxToken}
            onSelect={view.selectStation}
          />
        </section>

        <section className={"pane pane-chart" + (pane === "trend" ? " show" : "")}>
          <div className="chart-head">
            <h2>{chartTitle}</h2>
            {!selected && <span className="chart-hint">Select a site to filter</span>}
          </div>

          <div className="metric-tabs" role="tablist" aria-label="Metric">
            {METRIC_KEYS.map((k) => (
              <button
                key={k}
                role="tab"
                aria-selected={metric === k}
                className={metric === k ? "on" : ""}
                onClick={() => view.setParam("metric", k === DEFAULT_METRIC ? null : k)}
              >
                {METRICS[k].label}
              </button>
            ))}
          </div>

          <TrendChart
            series={series}
            bands={M.bands ? SCORE_BANDS : []}
            yDomain={M.domain}
            yLabel={M.unit}
            format={(v) => formatValue(v, M.digits)}
            emptyMessage="No scored collections here yet"
          />

          <div className="chart-foot">
            {series.length > 1
              ? series.slice(0, 8).map((s) => (
                <span className="chart-key" key={s.key}>
                  <i style={{ background: s.color }} /> {s.label}
                </span>
              ))
              : <span className="chart-key"><i style={{ background: M.color }} /> {series[0]?.label}</span>}
            <span className="count">{plural(pointCount, "point")}</span>
          </div>
        </section>

        {selected && (
          <section className={"pane pane-detail show" + (pane === "map" ? " show-narrow" : "")}>
            <StationDetail
              station={selected}
              rows={rowsInScope}
              url={view.permalink}
              streamLink={view.groupLink(selected.stream)}
              onClose={() => view.selectStation(null)}
              onStream={view.selectGroup}
            />
          </section>
        )}

        {!selected && activeStream && (
          <section
            className={"pane pane-detail is-group show" + (pane === "map" ? " show-narrow" : "")}
          >
            <div className="detail">
              <h2 className="detail-name">{activeStream}</h2>
              <p className="detail-sub">
                {plural(inScope.length, "monitoring site")}
                {(() => {
                  const ys = inScope.flatMap((s) => [s.firstYear, s.lastYear])
                    .filter((y): y is number => y !== null);
                  return ys.length ? ` · ${Math.min(...ys)}–${Math.max(...ys)}` : "";
                })()}
              </p>
              {(() => {
                // The stream's own annual grade, as the base rolls it up —
                // not an average of the station grades, which would weight
                // whichever sites happened to be sampled that year.
                const sy = data.streamYears
                  .filter((r) => r.stream === activeStream)
                  .sort((a, b) => a.y - b.y);
                const last = sy[sy.length - 1];
                if (!last) return null;
                const g = GRADES[last.grade];
                return (
                  <div className="reading" style={{ background: g.soft }}>
                    <div className="reading-head">
                      <span className="pill" style={{ background: g.color }}>
                        Stream grade {g.label}
                      </span>
                      <span className="reading-when">{last.y}</span>
                    </div>
                    <div className="reading-label">Total stream score</div>
                    <div className="reading-value" style={{ color: g.color }}>
                      {formatValue(last.score, 0)}<span className="reading-of">/100</span>
                    </div>
                    <p className="reading-blurb">{g.blurb}</p>
                  </div>
                );
              })()}
              <ShareCard
                url={view.groupLink(activeStream)}
                filename={activeStream.replace(/\W+/g, "-").toLowerCase()}
                heading={`Link to ${activeStream}`}
                blurb="Opens this dashboard filtered to this stream — for a partner page, a newsletter or a sign."
              />
            </div>
          </section>
        )}
      </main>

      <Footer
        items={[
          {
            label: "Updated",
            value: new Date(data.generatedAt).toLocaleString(undefined, {
              month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
            }),
          },
          { label: "Sites", value: String(data.stations.length) },
          { label: "Streams", value: String(data.streams.length) },
          {
            label: "Site-years",
            value: data.years.length.toLocaleString(),
            note: data.rowsExcluded ? `+${data.rowsExcluded} undated` : undefined,
            title: data.rowsExcluded
              ? `${data.rowsExcluded} rows in Airtable have no usable sample year and cannot be placed on a time axis.`
              : undefined,
          },
        ]}
        credit={<WaterWatchCredit />}
        right={
          <a href="https://watershedcouncil.org" target="_blank" rel="noreferrer">
            watershedcouncil.org
          </a>
        }
      />
    </div>
  );
}
